import TaxChangeRequestMethod, { hintOf, methodOf } from '@/components/covalent/tax/change/TaxChangeRequestMethod';
import { Input, Radio, RadioGroup, Row, Text } from '@/components/atoms';
import { Button, Divider, message, Modal, Tag } from 'antd';
import { DownloadOutlined, PlusOutlined } from '@ant-design/icons';
import {
  API_TAX_CONTACT,
  FORMAT_DATE_GENERAL_EXCEPT_TIME,
  IMAGE_PATH_TAX_CHANGE,
  IMAGE_PATH_TAX_CONTRACT,
  IMAGE_PATH_TAX_NOTICE,
} from '@/constants/constant';
import moment from 'moment';
import Select from '@/components/atoms/select/Select';
import SelectOption from '@/components/atoms/select/SelectOption';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { pcompany, ptax } from '@/proto/proto';
import api from '@/api';
import { makePdf } from '@/utils/fileExtension';

type TaxChangeRequestModalProps = {
  visible: boolean;
  allowExcept?: boolean;
  onCancel: () => void;
  onSend: (taxNotices: ptax.IPbTaxNotice[], method: TaxChangeRequestMethod, contactId: number) => void;
  onExcept?: (taxNotices: ptax.IPbTaxNotice[], label?: string) => void;
  title?: string;
  company: pcompany.IPbCompany;
  taxNotices?: ptax.IPbTaxNotice[];
};

const TaxChangeRequestModal = ({ visible, allowExcept, onCancel, onSend, onExcept, title, company, taxNotices }: TaxChangeRequestModalProps) => {
  const companyId = useMemo(() => {
    return Number(company?.base?.id ?? -1);
  }, [company]);

  const [method, setMethod] = useState(TaxChangeRequestMethod.EXCEPT);
  const [contacts, setContacts] = useState<ptax.IPbTaxContact[]>();
  const [contactId, setContactId] = useState(0);
  const [additionalContact, setAdditionalContact] = useState<ptax.IPbTaxContact>({
    companyId,
  });
  const [label, setLabel] = useState<any>();

  const changeFileName = useMemo(() => {
    if (taxNotices?.first()?.changeFileName) {
      return taxNotices?.first()?.changeFileOrgName;
    }
    return `${moment(taxNotices?.first()?.base?.createdAt).format(FORMAT_DATE_GENERAL_EXCEPT_TIME)}_${taxNotices?.first()?.plateNo}_민원신청서.pdf`;
  }, [taxNotices]);

  const faxContacts = useMemo(() => {
    return contacts?.filter((contact) => contact.fax);
  }, [contacts]);

  const govContacts = useMemo(() => {
    return contacts?.filter((contact) => contact.org);
  }, [contacts]);

  const getContacts = useCallback(() => {
    api
      .get(API_TAX_CONTACT, {
        params: {
          companyId,
        },
      })
      .then((response) => {
        setContacts(response.data.contact);
      });
  }, [companyId]);

  const addContact = useCallback(() => {
    api
      .post(API_TAX_CONTACT, 'ptax.PbTaxContact', additionalContact)
      .then((response) => {
        setAdditionalContact({ companyId });
        setMethod(TaxChangeRequestMethod.FAX);
        setContactId(Number(response.data?.base.id));
      })
      .catch(() => {
        message.error('이미 정보가 존재하는 기관입니다.');
      });
  }, [additionalContact, companyId]);

  useEffect(() => {
    setMethod(methodOf(taxNotices?.first()));
    setContactId(Number(taxNotices?.first()?.contact?.base?.id));
  }, [taxNotices]);

  useEffect(() => {
    getContacts();
  }, [getContacts, contactId]);

  return (
    <Modal
      visible={visible}
      title={title}
      onCancel={onCancel}
      onOk={() => {
        onCancel();
        switch (method) {
          case TaxChangeRequestMethod.FAX:
          case TaxChangeRequestMethod.GOV:
            onSend(taxNotices ?? [], method, contactId);
            break;
          case TaxChangeRequestMethod.EXCEPT:
            if (onExcept) {
              onExcept(taxNotices ?? [], label);
            }
            break;
          default:
            break;
        }
      }}
    >
      <Row justify="space-between">
        <Text>
          <Tag>고지서</Tag>
          {taxNotices?.first()?.fileOrgName}
        </Text>
        <Button icon={<DownloadOutlined />} onClick={() => window.open(IMAGE_PATH_TAX_NOTICE(companyId, taxNotices?.first()), '_self')}>
          다운로드
        </Button>
      </Row>
      <Row justify="space-between">
        <Text>
          <Tag>계약서</Tag>
          {taxNotices?.first()?.contractFileOrgName}
        </Text>
        <Button icon={<DownloadOutlined />} onClick={() => window.open(IMAGE_PATH_TAX_CONTRACT(companyId, taxNotices?.first()), '_self')}>
          다운로드
        </Button>
      </Row>
      <Row justify="space-between">
        <Text>
          <Tag>신청서</Tag>
          {changeFileName}
        </Text>
        <Button
          icon={<DownloadOutlined />}
          onClick={() => {
            if (taxNotices?.first()?.changeFileName) {
              window.open(IMAGE_PATH_TAX_CHANGE(companyId, taxNotices?.first()), '_self');
            } else {
              makePdf(taxNotices?.first(), company).then((pdf) => {
                pdf.save(changeFileName ?? '');
              });
            }
          }}
        >
          다운로드
        </Button>
      </Row>
      <RadioGroup
        optionType="button"
        buttonStyle="outline"
        value={method}
        defaultValue={taxNotices?.last()?.contract}
        onChange={({ target }) => setMethod(target.value)}
      >
        <Radio value={TaxChangeRequestMethod.GOV}>문서24</Radio>
        <Select
          showSearch
          placeholder={hintOf(TaxChangeRequestMethod.GOV)}
          value={govContacts?.first((contact) => contact.base?.id === contactId)?.name ?? undefined}
          onChange={(value) => {
            const found = contacts?.first((contact) => contact.name === value);
            if (found) {
              setMethod(TaxChangeRequestMethod.GOV);
              setContactId(Number(found.base?.id));
            }
          }}
        >
          {govContacts?.map((contact) => (
            <SelectOption key={`${contact.base?.id}`} value={contact.name ?? ''}>
              {contact.name}
            </SelectOption>
          ))}
        </Select>
        <Radio value={TaxChangeRequestMethod.FAX}>팩스</Radio>
        <Select
          showSearch
          placeholder={hintOf(TaxChangeRequestMethod.FAX)}
          value={faxContacts?.first((contact) => contact.base?.id === contactId)?.name ?? undefined}
          dropdownRender={(menu) => {
            return (
              <div>
                {menu}
                <Divider style={{ margin: '4px 0' }} />
                <div style={{ display: 'flex', flexWrap: 'nowrap', padding: 8 }}>
                  <Input
                    style={{ flex: 'auto', borderColor: '#181818', marginRight: '8px' }}
                    placeholder="기관이름"
                    value={additionalContact?.name ?? undefined}
                    onChange={({ target }) => setAdditionalContact({ ...additionalContact, name: target.value })}
                  />
                  <Input
                    style={{ flex: 'auto', borderColor: '#181818', marginRight: '8px' }}
                    placeholder="팩스번호"
                    value={additionalContact?.fax ?? undefined}
                    onChange={({ target }) => setAdditionalContact({ ...additionalContact, fax: target.value })}
                  />
                  {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions,jsx-a11y/anchor-is-valid */}
                  <a style={{ flex: 'none', margin: '8px', display: 'block', cursor: 'pointer' }} onClick={addContact}>
                    <PlusOutlined /> 추가
                  </a>
                </div>
              </div>
            );
          }}
          onChange={(value) => {
            const found = contacts?.first((contact) => contact.name === value);
            if (found) {
              setMethod(TaxChangeRequestMethod.FAX);
              setContactId(Number(found.base?.id));
            }
          }}
        >
          {faxContacts?.map((contact) => (
            <SelectOption key={`${contact.base?.id}`} value={contact.name ?? ''}>
              {contact.name}
            </SelectOption>
          ))}
        </Select>
        {allowExcept && (
          <>
            <Radio value={TaxChangeRequestMethod.EXCEPT}>기타</Radio>
            <Input
              placeholder={hintOf(TaxChangeRequestMethod.EXCEPT)}
              value={label}
              onChange={({ target }) => {
                setMethod(TaxChangeRequestMethod.EXCEPT);
                setLabel(target.value);
              }}
            />
          </>
        )}
      </RadioGroup>
    </Modal>
  );
};

TaxChangeRequestModal.defaultProps = {
  allowExcept: false,
  taxNotices: [],
  title: undefined,
  onExcept: undefined,
};

export default TaxChangeRequestModal;
